/**
 * Tipos compartilhados entre o front (apps/web) e a API (apps/api).
 * Fonte: docs/13_Contrato_da_API_REST.md
 *
 * Regra: o banco usa snake_case; a API usa camelCase. A conversão acontece
 * na camada de repositório, em um único lugar.
 */

// ---------------------------------------------------------------------------
// Enums de domínio — os valores são os canônicos do banco (docs/07 §3).
// Nunca traduzir: `approved` no banco, "Aprovado" na tela.
// ---------------------------------------------------------------------------

export const POST_STATUSES = [
  "draft",
  "pending_approval",
  "revision_requested",
  "approved",
  "needs_reschedule",
  "publishing",
  "published",
  "failed",
  "archived",
] as const;
export type PostStatus = (typeof POST_STATUSES)[number];

/** Método LL, movimento III: "cada post com função: aquecer, qualificar, vender". */
export const POST_PURPOSES = ["aquecer", "qualificar", "vender"] as const;
export type PostPurpose = (typeof POST_PURPOSES)[number];

export const MEDIA_TYPES = ["IMAGE", "CAROUSEL", "REELS"] as const;
export type MediaType = (typeof MEDIA_TYPES)[number];

/** As três ofertas da Lara, conforme o site: mentoria 1:1, consultoria, imersões. */
export const CLIENT_TIERS = ["mentoria", "consultoria", "imersao"] as const;
export type ClientTier = (typeof CLIENT_TIERS)[number];

export const CLIENT_STATUSES = ["active", "suspended", "archived"] as const;
export type ClientStatus = (typeof CLIENT_STATUSES)[number];

export const USER_ROLES = ["superadmin", "admin", "editor"] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const IG_ACCOUNT_STATUSES = ["connected", "expired", "revoked", "error"] as const;
export type IgAccountStatus = (typeof IG_ACCOUNT_STATUSES)[number];

export type ActorType = "user" | "client" | "system";

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export interface PostMediaDTO {
  id: string;
  position: number;
  kind: "image" | "video";
  publicUrl: string;
  thumbUrl: string | null;
  width: number | null;
  height: number | null;
}

export interface PostDTO {
  id: string;
  clientId: string;
  igAccountId: string | null;
  caption: string;
  mediaType: MediaType;
  purpose: PostPurpose;
  status: PostStatus;
  /** ISO-8601 UTC. A conversão para America/Sao_Paulo é responsabilidade do front (ADR-007). */
  scheduledAt: string | null;
  publishedAt: string | null;
  igPermalink: string | null;
  position: number;
  media: PostMediaDTO[];
  createdAt: string;
  updatedAt: string;
}

export interface IgAccountDTO {
  id: string;
  igUsername: string;
  accountType: string | null;
  status: IgAccountStatus;
  tokenExpiresAt: string;
  isDefault: boolean;
}

export interface ClientStatsDTO {
  pendingApproval: number;
  approved: number;
  published: number;
}

/**
 * Resumo de crescimento (docs 26/jul) — seguidores inicial/médio/total e
 * stories atual/média/próximos dias. "Médio" de seguidores é crescimento por
 * mês (current - baseline, normalizado por mês), não a média aritmética da
 * contagem. `null` em qualquer campo = ainda não há dado/medição suficiente.
 */
export interface ClientGrowthDTO {
  followersBaseline: number | null;
  followersCurrent: number | null;
  followersGrowthPerMonth: number | null;
  storyViewsCurrent: number | null;
  storyViewsAverage: number | null;
  storyViewsNextDays: number | null;
}

export interface ClientDTO {
  id: string;
  name: string;
  businessName: string | null;
  tier: ClientTier;
  status: ClientStatus;
  whatsapp: string | null;
  email: string | null;
  city: string | null;
  segment: string | null;
  /** Método LL, movimento II. Exibido ao lado dos posts na aprovação (RF-11). */
  positioning: string | null;
  /** null = ainda não existe briefing. Alimenta a coluna "Briefing pendente" do kanban de clientes. */
  briefingStatus: "draft" | "submitted" | null;
  igAccounts: IgAccountDTO[];
  stats: ClientStatsDTO;
  growth: ClientGrowthDTO;
  /** Marcação manual da Lara — "Perfil ajustado" no kanban de Gestão. null = ainda não marcado. */
  profileAdjustedAt: string | null;
  /** Estágio dinâmico no Kanban da agência. Padrão: 'novo' */
  kanbanStage: string;
  createdAt: string;
  updatedAt: string;
}

export interface KanbanColumnDTO {
  id: string;
  key: string;
  label: string;
  color: string;
  position: number;
  createdAt: string;
  updatedAt: string;
}

export interface SessionDTO {
  actorType: "user" | "client";
  role: UserRole | "client";
  name: string;
  clientId?: string;
  /** Só para actorType "user" — usado para travar telas restritas à conta da Lara. */
  email?: string;
}

// ---------------------------------------------------------------------------
// Envelopes de resposta
// ---------------------------------------------------------------------------

export interface ApiMeta {
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

export interface ApiSuccess<T> {
  data: T;
  meta?: ApiMeta;
}

export interface ApiErrorBody {
  error: {
    code: ApiErrorCode;
    /** Sempre em português e pronta para exibir ao usuário. */
    message: string;
    requestId: string;
    fields?: Record<string, string>;
  };
}

// ---------------------------------------------------------------------------
// Catálogo de erros — docs/13 §1.4
// ---------------------------------------------------------------------------

export const API_ERRORS = {
  VALIDATION_ERROR: { status: 400, message: "Confira os campos destacados." },
  UNAUTHENTICATED: { status: 401, message: "Sua sessão expirou. Entre novamente." },
  INVALID_CREDENTIALS: { status: 401, message: "E-mail ou senha incorretos." },
  FORBIDDEN: { status: 403, message: "Você não tem permissão para esta ação." },
  NOT_FOUND: { status: 404, message: "Não encontramos o que você procura." },
  MAGIC_LINK_INVALID: { status: 401, message: "Este link não é válido." },
  MAGIC_LINK_EXPIRED: { status: 401, message: "Este link expirou. Peça um novo acesso." },
  INVALID_TRANSITION: { status: 409, message: "Não é possível fazer isso agora." },
  POST_ALREADY_PUBLISHED: { status: 409, message: "Este post já foi publicado." },
  ALREADY_APPROVED: { status: 409, message: "Este post já foi aprovado." },
  IG_NOT_CONNECTED: { status: 409, message: "Conecte uma conta do Instagram antes." },
  IG_TOKEN_EXPIRED: { status: 409, message: "O acesso ao Instagram expirou. Reconecte a conta." },
  MEDIA_FORMAT_INVALID: { status: 422, message: "Envie imagens em JPEG." },
  MEDIA_TOO_LARGE: { status: 422, message: "Arquivo acima do limite permitido." },
  CAPTION_TOO_LONG: { status: 422, message: "A legenda passou de 2.200 caracteres." },
  SCHEDULE_TOO_OLD: { status: 422, message: "Esta data já passou. Escolha outra." },
  RATE_LIMITED: { status: 429, message: "Muitas tentativas. Aguarde alguns minutos." },
  INTERNAL_ERROR: { status: 500, message: "Algo deu errado. Já fomos avisados." },
} as const;

export type ApiErrorCode = keyof typeof API_ERRORS;

// ---------------------------------------------------------------------------
// Limites da API do Instagram — docs/11 §5
// ---------------------------------------------------------------------------

export const INSTAGRAM_LIMITS = {
  /** Limite de caracteres da legenda. */
  captionMaxLength: 2200,
  /** Itens em um carrossel. */
  carouselMin: 2,
  carouselMax: 10,
  /** Publicações por conta em uma janela móvel de 24 h. */
  postsPer24h: 100,
  /** A API aceita apenas JPEG para imagem. */
  allowedImageMimeTypes: ["image/jpeg"] as const,
  allowedVideoMimeTypes: ["video/mp4", "video/quicktime"] as const,
  maxImageBytes: 8 * 1024 * 1024,
  maxVideoBytes: 1024 * 1024 * 1024,
} as const;
